import Parse from "../parseClient";
import { getStoredScores, overwriteStoredScores } from "./scores";
import { getPlatform } from "./platform";
import { getPublisherId } from "./publisher";

function setAuthContextFields(user) {
  if (!user) return false;
  const platform = getPlatform(); // 'web' | 'ios' | 'android'
  const publisherId = getPublisherId();

  let didChange = false;

  // Always store latest context
  if (user.get("lastAuthPlatform") !== platform) {
    user.set("lastAuthPlatform", platform);
    didChange = true;
  }
  if (user.get("lastAuthPublisherId") !== (publisherId || null)) {
    user.set("lastAuthPublisherId", publisherId || null);
    didChange = true;
  }

  // Backfill first-known context if not set yet
  if (!user.get("signupPlatform")) {
    user.set("signupPlatform", platform);
    didChange = true;
  }
  if (user.get("signupPublisherId") === undefined) {
    // Allow explicit null (no publisher) to be stored once
    user.set("signupPublisherId", publisherId || null);
    didChange = true;
  }

  if (didChange) {
    return true;
  }
  return false;
}

async function persistAuthContext(user) {
  if (!user) return;
  const didChange = setAuthContextFields(user);
  if (!didChange) return;

  // Guard: saving a user without a valid session can produce misleading errors
  // (e.g. user already created server-side but client has no session token yet).
  const sessionToken = typeof user.getSessionToken === "function" ? user.getSessionToken() : null;
  if (!sessionToken) {
    console.warn("[Auth] Skipping auth context save: missing session token", { userId: user.id || null });
    return;
  }

  try {
    await user.save(null, { useMasterKey: false });
  } catch (e) {
    // Non-blocking: don't fail auth flows if this save fails
    console.warn("[Auth] Failed to persist auth context on user", e);
  }
}

function getClientLocaleData() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { language: null, region: null };
  }

  const primaryLocale =
    navigator.language ||
    (Array.isArray(navigator.languages) && navigator.languages.length > 0
      ? navigator.languages[0]
      : null);

  let language = null;
  let region = null;

  if (primaryLocale && typeof primaryLocale === "string") {
    const normalized = primaryLocale.trim().replace("_", "-");
    const parts = normalized.split("-");
    if (parts.length > 0 && parts[0]) {
      language = parts[0].toLowerCase();
    }
    if (parts.length > 1 && parts[1]) {
      region = parts[1].toUpperCase();
    }
  }

  if (!region && typeof Intl !== "undefined" && Intl.DateTimeFormat) {
    try {
      const options = Intl.DateTimeFormat().resolvedOptions();
      if (options?.locale) {
        const localeParts = options.locale.split("-");
        if (localeParts.length > 1 && localeParts[1]) {
          region = localeParts[1].toUpperCase();
        }
      }
    } catch (error) {
      console.warn("Unable to derive region from Intl API", error);
    }
  }

  return {
    language: language || null,
    region: region || null,
  };
}

function applyLocaleDataToUser(user) {
  if (!user) return;
  const { language, region } = getClientLocaleData();
  let didChange = false;

  const currentLanguage = user.get("language");
  const currentRegion = user.get("region");

  // Only update if there's an actual change (not just undefined/null differences)
  // This prevents unnecessary saves when user just opens the app
  if (language && currentLanguage !== language) {
    console.log("[Locale] Language changed:", { from: currentLanguage, to: language, userId: user.id });
    user.set("language", language);
    didChange = true;
  }
  if (region && currentRegion !== region) {
    console.log("[Locale] Region changed:", { from: currentRegion, to: region, userId: user.id });
    user.set("region", region);
    didChange = true;
  }

  // Only save if there was an actual change
  // This prevents updatedAt from changing when user just opens the app without playing
  if (didChange) {
    console.log("[Locale] Saving user due to locale change:", { userId: user.id, language, region });
    user.save().catch((error) => {
      console.warn("Failed to persist locale data", error);
    });
  } else {
    // Log when function is called but no changes are needed (for debugging)
    console.log("[Locale] No locale changes needed:", { userId: user.id, currentLanguage, currentRegion, detectedLanguage: language, detectedRegion: region });
  }
}

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function normalizeName(name) {
  return String(name || "").trim();
}

/**
 * Adds a user to Mailchimp audience via Parse Cloud Function
 * Uses Cloud Function to avoid CORS issues with Mailchimp API
 * @param {string} email - User email address
 * @param {string} name - User name
 * @returns {Promise<void>}
 */
async function addUserToMailchimp(email, name) {
  if (!email || !email.trim()) {
    return;
  }

  try {
    // Call Parse Cloud Function instead of direct API call
    // This avoids CORS issues and keeps API keys secure on the server
    const result = await Parse.Cloud.run('addUserToMailchimp', {
      email: email.trim(),
      name: (name || '').trim(),
    });

    if (result && result.success) {
      console.log('[Mailchimp] Successfully added user to audience');
    } else {
      console.warn('[Mailchimp] Failed to add user:', result?.message || 'Unknown error');
    }
  } catch (error) {
    // Don't throw - we don't want Mailchimp failures to block user signup
    console.warn('[Mailchimp] Failed to add user to audience:', error.message || error);
  }
}

// FEATURE: UNIQUE_NAME_VALIDATION
// Feature flag to enable/disable unique name validation
const UNIQUE_NAME_VALIDATION_ENABLED = true;

/**
 * Validates if a name is available (case-insensitive)
 * @param {string} name - The name to check
 * @param {string|null} excludeUserId - User ID to exclude from check (for updates)
 * @returns {Promise<{available: boolean, suggestion: string|null}>}
 */
export async function validateNameUniqueness(name, excludeUserId = null) {
  // Feature flag check
  if (!UNIQUE_NAME_VALIDATION_ENABLED) {
    return { available: true, suggestion: null };
  }

  const trimmedName = normalizeName(name);
  
  // Skip validation for empty or very short names
  if (trimmedName.length < 2) {
    return { available: true, suggestion: null };
  }

  try {
    const result = await Parse.Cloud.run("checkNameAvailability", {
      name: trimmedName,
      excludeUserId: excludeUserId || null,
    });
    
    return result;
  } catch (error) {
    console.warn("Name validation error:", error);
    // Fail open: allow the name if validation fails
    return { available: true, suggestion: null };
  }
}
// END FEATURE: UNIQUE_NAME_VALIDATION

const FINISHED_IDS_KEY = "finishedIdNumbers";

function readLocalFinishedIds() {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem("finishedGameOutcomes");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return [];
    return Object.keys(parsed)
      .map((k) => Number(k))
      .filter((n) => Number.isFinite(n));
  } catch (error) {
    return [];
  }
}

export async function saveUserScores({ score, streak, maxStreak, numbersPlayed, monthlyIncrement, lastWonId, lastPlayedId, lastScore, incrementPlayedCount = false }) {
  const user = Parse.User.current();
  if (!user) return;
  if (typeof score === "number") user.set("score", score);
  if (typeof streak === "number") user.set("streak", streak);
  if (typeof maxStreak === "number") user.set("maxStreak", maxStreak);
  if (typeof numbersPlayed === "number") user.set("numbersPlayed", numbersPlayed);
  
  // Save lastScore if it's explicitly provided (not undefined)
  // - If it's a number (0-10): update it (win or loss case - 0 for loss, 0-10 for win)
  // - If it's null: clear it (explicit reset, rare case)
  // - If it's undefined: don't change it (preserve existing value)
  if (typeof lastScore === "number" && Number.isFinite(lastScore) && lastScore >= 0 && lastScore <= 10) {
    user.set("lastScore", lastScore);
    console.log("Setting lastScore on user:", lastScore);
  } else if (lastScore === null) {
    // Only set to null if explicitly provided (should be rare, only for explicit resets)
    user.set("lastScore", null);
    console.log("Setting lastScore to null on user (explicit reset)");
  }
  // If lastScore is undefined, don't change it (preserve existing value)

  // Automatic detection possibleCheater
  // Check if flag was set in updateScores (perfect game detected)
  const shouldSetPossibleCheater = localStorage.getItem("shouldSetPossibleCheater") === "true";
  const currentPossibleCheater = user.get("possibleCheater");
  
  const flagValue = localStorage.getItem("shouldSetPossibleCheater");
  console.log("[PossibleCheater] Checking flag in saveUserScores:", {
    userId: user.id,
    shouldSetPossibleCheater,
    currentPossibleCheater,
    flagValue,
    allRelevantKeys: Object.keys(localStorage).filter(k => k.toLowerCase().includes("cheater") || k.toLowerCase().includes("score")),
  });
  
  if (shouldSetPossibleCheater) {
    const wasAlreadyCheater = currentPossibleCheater === true;
    
    // If user is already marked as possibleCheater, don't set it again and clear the flag
    if (wasAlreadyCheater) {
      console.log("[PossibleCheater] User already flagged as possible cheater, clearing flag and skipping update", {
        userId: user.id,
      });
      localStorage.removeItem("shouldSetPossibleCheater");
    } else {
      console.log("[PossibleCheater] ✅ Flag is set! Setting possibleCheater on user:", {
        userId: user.id,
        wasAlreadyCheater,
        currentPossibleCheater,
      });
      
      // Set the field on the user object
      // Use explicit boolean true, not just truthy value
      user.set("possibleCheater", Boolean(true));
      
      // Verify it's actually set
      const testGet = user.get("possibleCheater");
      console.log("[PossibleCheater] Immediately after user.set:", {
        testGet,
        type: typeof testGet,
        isBoolean: testGet === true,
      });
      
      // Remove flag after verifying it's set (keep for debugging until save succeeds)
      // We'll remove it after successful save
      
      // Verify it was set BEFORE save
      const afterSet = user.get("possibleCheater");
      const dirtyKeys = user.dirtyKeys ? user.dirtyKeys() : [];
      const isInDirtyKeys = dirtyKeys.includes("possibleCheater");
      
      console.log("[PossibleCheater] After user.set, before save:", {
        userId: user.id,
        afterSet,
        isInDirtyKeys,
        allDirtyKeys: dirtyKeys,
      });
      
      console.log("[PossibleCheater] ⚠️⚠️⚠️ Setting possibleCheater flag on user:", {
        userId: user.id,
        username: user.get("username"),
        name: user.get("name"),
        afterSet,
      });
    }
  } else {
    console.log("[PossibleCheater] ❌ Flag NOT set, skipping possibleCheater update", {
      userId: user.id,
      flagValue,
      shouldSetPossibleCheater,
      checkFlagAgain: localStorage.getItem("shouldSetPossibleCheater"),
    });
  }

  
  // Only save lastWonId if it's explicitly provided (not undefined)
  // - If it's a number: update it (win case)
  // - If it's null: clear it (explicit reset, rare case)
  // - If it's undefined: don't change it (loss case - preserve existing value)
  if (typeof lastWonId === "number" && Number.isFinite(lastWonId)) {
    user.set("lastWonId", lastWonId);
    console.log("Setting lastWonId on user:", lastWonId);
  } else if (lastWonId === null) {
    // Only set to null if explicitly provided (should be rare, only for explicit resets)
    user.set("lastWonId", null);
    console.log("Setting lastWonId to null on user (explicit reset)");
  }
  // If lastWonId is undefined, don't change it (preserve existing value on loss)
  
  // Save lastPlayedId if it's explicitly provided (not undefined)
  // - If it's a number: update it (always update when a game is played)
  // - If it's null: clear it (explicit reset, rare case)
  // - If it's undefined: don't change it (preserve existing value)
  if (typeof lastPlayedId === "number" && Number.isFinite(lastPlayedId)) {
    user.set("lastPlayedId", lastPlayedId);
    console.log("Setting lastPlayedId on user:", lastPlayedId);
  } else if (lastPlayedId === null) {
    // Only set to null if explicitly provided (should be rare, only for explicit resets)
    user.set("lastPlayedId", null);
    console.log("Setting lastPlayedId to null on user (explicit reset)");
  }
  // If lastPlayedId is undefined, don't change it (preserve existing value)
  
  if (typeof monthlyIncrement === "number" && Number.isFinite(monthlyIncrement) && monthlyIncrement !== 0) {
    const { fieldName } = getCurrentMonthScoreField();
    const currentValue = Number(user.get(fieldName)) || 0;
    user.set(fieldName, currentValue + monthlyIncrement);
  }
  
  // Increment playedMonthOdd or playedMonthEven when a game is finished
  // This should only be called when finishing a game (win or lose)
  if (incrementPlayedCount) {
    const { isEvenMonth } = getCurrentMonthScoreField();
    const playedFieldName = isEvenMonth ? "playedMonthEven" : "playedMonthOdd";
    const currentPlayed = Number(user.get(playedFieldName)) || 0;
    user.set(playedFieldName, currentPlayed + 1);
  }
  
  try {
    const possibleCheaterBeforeSave = user.get("possibleCheater");
    const dirtyKeysBeforeSave = user.dirtyKeys ? user.dirtyKeys() : [];
    const shouldSetFlag = localStorage.getItem("shouldSetPossibleCheater") === "true";
    
    console.log("[PossibleCheater] About to save user:", {
      userId: user.id,
      possibleCheaterBeforeSave,
      shouldSetFlag,
      isInDirtyKeys: dirtyKeysBeforeSave.includes("possibleCheater"),
      allDirtyKeys: dirtyKeysBeforeSave,
    });
    
    await user.save(null, { useMasterKey: false });
    
    console.log("[PossibleCheater] User.save() completed, now fetching fresh data...");
    
    // Fetch fresh user data to see if it was actually saved
    await user.fetch();
    
    const possibleCheaterAfterSave = user.get("possibleCheater");
    
    console.log("[PossibleCheater] User saved and fetched:", {
      userId: user.id,
      possibleCheaterBeforeSave,
      possibleCheaterAfterSave,
      lastWonId: user.get("lastWonId"),
      wasChanged: possibleCheaterBeforeSave !== possibleCheaterAfterSave,
      shouldHaveBeenSet: shouldSetFlag,
    });
    
    // Verify if possibleCheater was set correctly
    if (shouldSetFlag && possibleCheaterBeforeSave !== true && possibleCheaterAfterSave === true) {
      console.log("[PossibleCheater] ✅✅✅ SUCCESS: possibleCheater flag successfully saved to server!");
      // Only remove flag after successful save
      localStorage.removeItem("shouldSetPossibleCheater");
    } else if (shouldSetFlag && possibleCheaterAfterSave !== true) {
      console.error("[PossibleCheater] ❌❌❌ FAILED: Flag was set but possibleCheater was NOT saved to Parse!", {
        before: possibleCheaterBeforeSave,
        after: possibleCheaterAfterSave,
        shouldHaveBeenSet: shouldSetFlag,
        userId: user.id,
      });
      console.warn("[PossibleCheater] This might mean the 'possibleCheater' field doesn't exist in Parse schema. Check Back4App dashboard.");
    } else if (possibleCheaterBeforeSave === true && possibleCheaterAfterSave === true) {
      console.log("[PossibleCheater] ✅ possibleCheater flag was already set and remains set");
      // User is already marked as possibleCheater, clear the flag to prevent re-setting
      if (shouldSetFlag) {
        localStorage.removeItem("shouldSetPossibleCheater");
        console.log("[PossibleCheater] Cleared shouldSetPossibleCheater flag (user already marked)");
      }
    } else {
      console.log("[PossibleCheater] ℹ️ possibleCheater unchanged:", {
        before: possibleCheaterBeforeSave,
        after: possibleCheaterAfterSave,
        shouldHaveBeenSet: shouldSetFlag,
      });
      // If flag was set but user is already marked, clear it
      if (shouldSetFlag && possibleCheaterAfterSave === true) {
        localStorage.removeItem("shouldSetPossibleCheater");
        console.log("[PossibleCheater] Cleared shouldSetPossibleCheater flag (user already marked)");
      }
    }
  } catch (error) {
    console.error("Failed to save user scores to Parse:", error);
    // Re-throw so caller can handle it (e.g., store as pending save)
    throw error;
  }
}

async function migrateLocalProgressIntoUser(user) {
  if (!user || typeof window === "undefined") return;
  try {
    const migrationKey = `migratedUser:${user.id}:v1`;
    if (window.localStorage.getItem(migrationKey) === "true") {
      return;
    }

    const local = getStoredScores();
    const serverScore = Number(user.get("score")) || 0;
    const serverStreak = Number(user.get("streak")) || 0;
    const serverMaxStreak = Number(user.get("maxStreak")) || 0;
    const serverNumbersPlayed = Number(user.get("numbersPlayed")) || 0;
    const serverLastWonId = Number.isFinite(user.get("lastWonId")) ? Number(user.get("lastWonId")) : null;
    const localLastWonId = Number.isFinite(local.lastWonId) ? Number(local.lastWonId) : null;
    const serverLastPlayedId = Number.isFinite(user.get("lastPlayedId")) ? Number(user.get("lastPlayedId")) : null;
    const localLastPlayedId = Number.isFinite(local.lastPlayedId) ? Number(local.lastPlayedId) : null;
    
    // Get month-dependent played counts
    const serverPlayedMonthOdd = Number(user.get("playedMonthOdd")) || 0;
    const serverPlayedMonthEven = Number(user.get("playedMonthEven")) || 0;
    const localPlayedMonthOdd = Number(local.playedMonthOdd) || 0;
    const localPlayedMonthEven = Number(local.playedMonthEven) || 0;

    // Merge totals by taking the max for persistent totals
    const mergedScore = Math.max(serverScore, Number(local.score) || 0);
    const mergedStreak = Math.max(serverStreak, Number(local.streak) || 0);
    const mergedMaxStreak = Math.max(serverMaxStreak, Number(local.maxStreak) || 0);
    const mergedNumbersPlayed = Math.max(serverNumbersPlayed, Number(local.numbersPlayed) || 0);
    
    // Merge month-dependent played counts by taking the max
    const mergedPlayedMonthOdd = Math.max(serverPlayedMonthOdd, localPlayedMonthOdd);
    const mergedPlayedMonthEven = Math.max(serverPlayedMonthEven, localPlayedMonthEven);
    
    // Merge lastWonId: take the highest (most recent) value
    const mergedLastWonId = (serverLastWonId !== null && localLastWonId !== null)
      ? Math.max(serverLastWonId, localLastWonId)
      : (serverLastWonId !== null ? serverLastWonId : localLastWonId);
    
    // Merge lastPlayedId: take the highest (most recent) value
    const mergedLastPlayedId = (serverLastPlayedId !== null && localLastPlayedId !== null)
      ? Math.max(serverLastPlayedId, localLastPlayedId)
      : (serverLastPlayedId !== null ? serverLastPlayedId : localLastPlayedId);

    // Merge finished ids (unique union)
    const existingFinished = Array.isArray(user.get(FINISHED_IDS_KEY)) ? user.get(FINISHED_IDS_KEY) : [];
    const localFinished = readLocalFinishedIds();
    const unionSet = new Set([...(existingFinished || []), ...localFinished]);
    const mergedFinished = Array.from(unionSet).filter((n) => Number.isFinite(n));

    // Persist merges
    // NOTE: scoreMonthOdd/scoreMonthEven are NEVER updated during login migration
    // They remain as-is on the server and are not affected by local scores
    user.set("score", mergedScore);
    user.set("streak", mergedStreak);
    user.set("maxStreak", mergedMaxStreak);
    user.set("numbersPlayed", mergedNumbersPlayed);
    user.set("playedMonthOdd", mergedPlayedMonthOdd);
    user.set("playedMonthEven", mergedPlayedMonthEven);
    if (mergedLastWonId !== null) {
      user.set("lastWonId", mergedLastWonId);
    }
    if (mergedLastPlayedId !== null) {
      user.set("lastPlayedId", mergedLastPlayedId);
    }
    user.set(FINISHED_IDS_KEY, mergedFinished);
    await user.save();

    // Update local storage to reflect server after migration
    overwriteStoredScores({
      score: mergedScore,
      streak: mergedStreak,
      maxStreak: mergedMaxStreak,
      numbersPlayed: mergedNumbersPlayed,
      lastPlayedId: mergedLastPlayedId,
      lastWonId: mergedLastWonId,
      playedMonthOdd: mergedPlayedMonthOdd,
      playedMonthEven: mergedPlayedMonthEven,
    });

    window.localStorage.setItem(migrationKey, "true");
  } catch (error) {
    console.warn("Failed migrating local progress into user", error);
  }
}

export async function signUpUser({ name, email, password }) {
  const trimmedName = normalizeName(name);
  const trimmedEmail = normalizeEmail(email);
  const rawPassword = String(password || "");

  if (!trimmedName) {
    throw new Error("Please enter your name.");
  }
  if (!trimmedEmail) {
    throw new Error("Please enter your email.");
  }
  if (!rawPassword) {
    throw new Error("Please enter a password.");
  }

  // FEATURE: UNIQUE_NAME_VALIDATION
  if (UNIQUE_NAME_VALIDATION_ENABLED) {
    const validation = await validateNameUniqueness(trimmedName);
    if (!validation.available) {
      const suggestionText = validation.suggestion 
        ? ` Try "${validation.suggestion}" instead.`
        : "";
      throw new Error(`This name is already taken.${suggestionText}`);
    }
  }
  // END FEATURE: UNIQUE_NAME_VALIDATION

  const user = new Parse.User();
  user.set("username", trimmedEmail);
  user.set("password", rawPassword);
  user.set("email", trimmedEmail);
  user.set("name", trimmedName);
  const { language, region } = getClientLocaleData();
  if (language) {
    user.set("language", language);
  }
  if (region) {
    user.set("region", region);
  }

  // Track platform + publisher on account creation
  // IMPORTANT: do not call user.save() before signUp() (can create a user without a session token).
  setAuthContextFields(user);

  await user.signUp();
  // Persist context fields now that we have a session token (non-blocking on failure)
  await persistAuthContext(user);

  // FEATURE: BATTLE_MODE - Link Installation to user for push notifications
  try {
    const { linkInstallationToUser } = await import('./notificationService');
    await linkInstallationToUser();
  } catch (error) {
    console.warn('[Auth] Failed to link Installation to user:', error);
  }
  // END FEATURE: BATTLE_MODE

  // Add user to Mailchimp audience (non-blocking)
  // Note: This is only for regular signup, not Apple Sign In
  addUserToMailchimp(trimmedEmail, trimmedName).catch((mailchimpError) => {
    // Already handled in addUserToMailchimp, but catch here to be safe
    console.warn('[Mailchimp] Error in signup flow:', mailchimpError);
  });

  try {
    // First, set base totals from local (but NOT monthly scores - those start fresh)
    const storedScores = getStoredScores();
    await saveUserScores({
      score: storedScores.score,
      streak: storedScores.streak,
      maxStreak: storedScores.maxStreak,
      numbersPlayed: storedScores.numbersPlayed,
      // Don't set monthlyIncrement - monthly scores start fresh for new accounts
    });
    // Then merge finished ids and finalize merged state
    await migrateLocalProgressIntoUser(user);
  } catch (mergeError) {
    console.warn("Failed to migrate local data after sign up", mergeError);
  }

  return {
    email: trimmedEmail,
  };
}

export async function signInUser({ email, password }) {
  const trimmedInput = String(email || "").trim();
  const rawPassword = String(password || "");

  if (!trimmedInput || !rawPassword) {
    throw new Error("Please enter your email/name and password.");
  }

  // FEATURE: LOGIN_WITH_NAME_OR_EMAIL
  // Support login with either email or name
  let loginUsername = null;
  
  // Check if input is an email (contains @)
  const isEmail = trimmedInput.includes("@");
  
  if (isEmail) {
    // Use email directly (normalized to lowercase)
    loginUsername = normalizeEmail(trimmedInput);
  } else {
    // Input is a name - use Cloud Function to find username by name
    try {
      const result = await Parse.Cloud.run("findUsernameByName", {
        name: trimmedInput,
      });
      
      if (!result || !result.found || !result.username) {
        throw new Error("No account found with this name or email.");
      }
      
      loginUsername = result.username;
    } catch (error) {
      if (error.message && error.message.includes("No account found")) {
        throw error;
      }
      console.error("Error finding user by name:", error);
      throw new Error("No account found with this name or email.");
    }
  }
  
  if (!loginUsername) {
    throw new Error("Please enter a valid email or name.");
  }
  // END FEATURE: LOGIN_WITH_NAME_OR_EMAIL

  const user = await Parse.User.logIn(loginUsername, rawPassword);

  try {
    await user.fetch();
    // Fetch posibleCheater field for current user
    const possibleCheater = user.get("possibleCheater") ?? false;
  } catch (error) {
    console.warn("Failed to refresh user after sign in", error);
  }

  // Track platform + publisher on login (and backfill if needed)
  await persistAuthContext(user);

  if (user) {
    // FEATURE: BATTLE_MODE - Link Installation to user for push notifications
    try {
      const { linkInstallationToUser } = await import('./notificationService');
      await linkInstallationToUser();
    } catch (error) {
      console.warn('[Auth] Failed to link Installation to user:', error);
    }
    // END FEATURE: BATTLE_MODE
    
    applyLocaleDataToUser(user);

    // Merge local progress into the account (one-time)
    await migrateLocalProgressIntoUser(user);

    // Refresh local storage from server after merge
    const stored = getStoredScores();
    const serverLastWonId = Number.isFinite(user.get("lastWonId")) ? Number(user.get("lastWonId")) : null;
    const serverLastPlayedId = Number.isFinite(user.get("lastPlayedId")) ? Number(user.get("lastPlayedId")) : null;
    const serverScores = {
      score: user.get("score") ?? 0,
      streak: user.get("streak") ?? 0,
      maxStreak: user.get("maxStreak") ?? 0,
      numbersPlayed: user.get("numbersPlayed") ?? 0,
      lastPlayedId: serverLastPlayedId !== null ? serverLastPlayedId : (stored.lastPlayedId ?? null),
      // Use server lastWonId if available, otherwise fall back to local (for backward compatibility)
      lastWonId: serverLastWonId !== null ? serverLastWonId : (stored.lastWonId ?? null),
      playedMonthOdd: user.get("playedMonthOdd") ?? 0,
      playedMonthEven: user.get("playedMonthEven") ?? 0,
    };
    overwriteStoredScores(serverScores);
  }

  return user;
}

export function getCurrentUser() {
  return Parse.User.current();
}

// Check if running in Capacitor iOS
function isCapacitorIOS() {
  if (typeof window === "undefined" || !window.Capacitor) return false;
  return window.Capacitor.getPlatform() === 'ios';
}

// Apple Sign In with Parse integration
export async function signInWithApple() {
  if (!isCapacitorIOS()) {
    throw new Error("Apple Sign In is only available on iOS");
  }

  try {
    // Use npm package for Apple Sign In
    const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
    
    const result = await SignInWithApple.authorize({
      scopes: 'email name',
    });

    if (!result || !result.response) {
      throw new Error("Apple Sign In was cancelled or failed");
    }

    const response = result.response;
    const identityToken = response.identityToken;
    const appleUserId = response.user || response.userIdentifier;
    const email = response.email || null;
    const givenName = response.givenName || null;
    const familyName = response.familyName || null;
    
    if (!identityToken || !appleUserId) {
      throw new Error("Apple Sign In did not return required credentials");
    }

    // Store Apple Sign-In details immediately for auto-sign-in on restart
    if (typeof window !== "undefined") {
      window.localStorage.setItem('appleSignInUserId', appleUserId);
      if (identityToken) {
        const idTokenString = typeof identityToken === 'string' 
          ? identityToken 
          : String(identityToken);
        window.localStorage.setItem('appleSignInIdentityToken', idTokenString);
      }
      if (email) {
        window.localStorage.setItem('appleSignInEmail', email);
      }
      if (givenName) {
        window.localStorage.setItem('appleSignInGivenName', givenName);
      }
      if (familyName) {
        window.localStorage.setItem('appleSignInFamilyName', familyName);
      }
      console.log("Stored Apple Sign-In details locally");
    }

    // Parse JavaScript SDK doesn't have signUpWith/logInWith methods
    // Use manual account creation with appleId as identifier
    console.log("Creating/signing in Apple user account...");
    
    // Check if user already exists by appleId using Cloud Function (has master key access)
    let parseUser = null;
    let userUsername = null;
    
    try {
      const cloudResult = await Parse.Cloud.run('findUserByAppleId', { appleId: appleUserId });
      if (cloudResult && cloudResult.found) {
        console.log("Found existing user by appleId via Cloud Function:", cloudResult.userId);
        userUsername = cloudResult.username;
        
        // Fetch the user object
        const userQuery = new Parse.Query(Parse.User);
        userQuery.equalTo('objectId', cloudResult.userId);
        const users = await userQuery.find();
        if (users.length > 0) {
          parseUser = users[0];
        }
      }
    } catch (cloudError) {
      console.warn("Error using Cloud Function to find user by appleId:", cloudError);
      // Fallback: try direct query
      try {
        const appleQuery = new Parse.Query(Parse.User);
        appleQuery.equalTo('appleId', appleUserId);
        const appleUsers = await appleQuery.find();
        if (appleUsers.length > 0) {
          parseUser = appleUsers[0];
          userUsername = parseUser.get('username');
          console.log("Found existing user by appleId (direct query):", parseUser.id);
        }
      } catch (appleError) {
        console.warn("Error querying by appleId:", appleError);
        // Continue - will try to create new account
      }
    }

    // If user exists with this appleId, log them in
    if (parseUser) {
      console.log("Existing user found with appleId, logging in...", parseUser.id);
      
      // Get stored credentials for login
      const storedUsername = typeof window !== "undefined" ? window.localStorage.getItem('appleSignInUsername') : null;
      const storedPassword = typeof window !== "undefined" ? window.localStorage.getItem('appleSignInPassword') : null;
      
      if (!userUsername) {
        userUsername = parseUser.get('username');
      }
      
      // Try to log in with stored credentials first
      if (storedUsername && storedPassword && storedUsername === userUsername) {
        try {
          console.log("Logging in with stored credentials:", storedUsername);
          const loggedInUser = await Parse.User.logIn(storedUsername, storedPassword);
          console.log("Successfully logged in with stored credentials");
          
          // Refresh user data
          try {
            await loggedInUser.fetch();
            // Fetch posibleCheater field for current user
            const possibleCheater = loggedInUser.get("possibleCheater") ?? false;
          } catch (error) {
            console.warn("Failed to refresh user:", error);
          }
          
          await persistAuthContext(loggedInUser);
          applyLocaleDataToUser(loggedInUser);
          await migrateLocalProgressIntoUser(loggedInUser);
          
          // Refresh local storage from server
          const stored = getStoredScores();
          const serverLastWonId = Number.isFinite(loggedInUser.get("lastWonId")) ? Number(loggedInUser.get("lastWonId")) : null;
          const serverLastPlayedId = Number.isFinite(loggedInUser.get("lastPlayedId")) ? Number(loggedInUser.get("lastPlayedId")) : null;
          const serverScores = {
            score: loggedInUser.get("score") ?? 0,
            streak: loggedInUser.get("streak") ?? 0,
            maxStreak: loggedInUser.get("maxStreak") ?? 0,
            numbersPlayed: loggedInUser.get("numbersPlayed") ?? 0,
            lastPlayedId: serverLastPlayedId !== null ? serverLastPlayedId : (stored.lastPlayedId ?? null),
            lastWonId: serverLastWonId !== null ? serverLastWonId : (stored.lastWonId ?? null),
            playedMonthOdd: loggedInUser.get("playedMonthOdd") ?? 0,
            playedMonthEven: loggedInUser.get("playedMonthEven") ?? 0,
          };
          overwriteStoredScores(serverScores);
          
          // Store Apple Sign In state for auto-login
          if (typeof window !== "undefined") {
            window.localStorage.setItem('appleSignInUserId', appleUserId);
            window.localStorage.setItem('appleSignInUsername', userUsername);
            if (storedPassword) {
              window.localStorage.setItem('appleSignInPassword', storedPassword);
            }
          }
          
          console.log("Successfully logged in existing user:", loggedInUser.id);
          return loggedInUser;
        } catch (loginError) {
          console.warn("Login with stored credentials failed:", loginError);
          // Fallback: try to set user manually
        }
      }
      
      // If login with credentials failed or no credentials, try to fetch and set user
      try {
        // Try to fetch the user to ensure we have the latest data
        await parseUser.fetch();
        // Fetch posibleCheater field for current user
        const possibleCheater = parseUser.get("possibleCheater") ?? false;
        
        // Set user as current
        Parse.User._clearCache();
        Parse.User._setCurrentUserCache(parseUser);
        
        // Verify user is current
        let currentUser = Parse.User.current();
        if (!currentUser || currentUser.id !== parseUser.id) {
          Parse.User._clearCache();
          Parse.User._setCurrentUserCache(parseUser);
          currentUser = parseUser;
        }
        
        await persistAuthContext(currentUser);
        applyLocaleDataToUser(currentUser);
        await migrateLocalProgressIntoUser(currentUser);
        
        // Refresh local storage from server
        const stored = getStoredScores();
        const serverLastWonId = Number.isFinite(currentUser.get("lastWonId")) ? Number(currentUser.get("lastWonId")) : null;
        const serverLastPlayedId = Number.isFinite(currentUser.get("lastPlayedId")) ? Number(currentUser.get("lastPlayedId")) : null;
        const serverScores = {
          score: currentUser.get("score") ?? 0,
          streak: currentUser.get("streak") ?? 0,
          maxStreak: currentUser.get("maxStreak") ?? 0,
          numbersPlayed: currentUser.get("numbersPlayed") ?? 0,
          lastPlayedId: serverLastPlayedId !== null ? serverLastPlayedId : (stored.lastPlayedId ?? null),
          lastWonId: serverLastWonId !== null ? serverLastWonId : (stored.lastWonId ?? null),
        };
        overwriteStoredScores(serverScores);
        
        // Store Apple Sign In state for auto-login
        if (typeof window !== "undefined") {
          window.localStorage.setItem('appleSignInUserId', appleUserId);
          if (userUsername) {
            window.localStorage.setItem('appleSignInUsername', userUsername);
          }
          // Try to get password from stored credentials or generate a new session token
          const storedPwd = typeof window !== "undefined" ? window.localStorage.getItem('appleSignInPassword') : null;
          if (storedPwd) {
            window.localStorage.setItem('appleSignInPassword', storedPwd);
          }
        }
        
        console.log("Successfully set existing user as current:", currentUser.id);
        return currentUser;
      } catch (fetchError) {
        console.warn("Failed to fetch/set existing user:", fetchError);
        // If we can't fetch the user, it might not exist anymore - continue to create new account
        parseUser = null;
      }
    }

    // User doesn't exist - create new account
    console.log("No existing user found with appleId, creating new account...");
    
    // Create username based on appleId to ensure uniqueness
    const username = `apple_${appleUserId}`;
    
    const newUser = new Parse.User();
    // Generate a secure random password for the account
    const randomPassword = Array.from(crypto.getRandomValues(new Uint8Array(32)))
      .map(b => b.toString(16).padStart(2, '0')).join('');
    
    newUser.set('username', username);
    newUser.set('password', randomPassword);
    newUser.set('appleId', appleUserId);
    newUser.set('authProvider', 'apple');
    
    if (email) {
      newUser.set('email', email);
    }
    
    const name = [givenName, familyName].filter(Boolean).join(' ') || 'Apple User';
    
    // FEATURE: UNIQUE_NAME_VALIDATION
    if (UNIQUE_NAME_VALIDATION_ENABLED) {
      const validation = await validateNameUniqueness(name);
      if (!validation.available && validation.suggestion) {
        // Use suggestion if name is taken
        newUser.set('name', validation.suggestion);
      } else {
        newUser.set('name', name);
      }
    } else {
      newUser.set('name', name);
    }
    // END FEATURE: UNIQUE_NAME_VALIDATION
    
    const { language, region } = getClientLocaleData();
    if (language) newUser.set('language', language);
    if (region) newUser.set('region', region);

    try {
      await persistAuthContext(newUser);
      await newUser.signUp();

      // Store credentials for future auto-login
      if (typeof window !== "undefined") {
        window.localStorage.setItem('appleSignInUserId', appleUserId);
        window.localStorage.setItem('appleSignInPassword', randomPassword);
        window.localStorage.setItem('appleSignInUsername', username);
      }

      // Migrate local progress (but NOT monthly scores - those start fresh for new accounts)
      const storedScores = getStoredScores();
      await saveUserScores({
        score: storedScores.score,
        streak: storedScores.streak,
        maxStreak: storedScores.maxStreak,
        numbersPlayed: storedScores.numbersPlayed,
        // Don't set monthlyIncrement - monthly scores start fresh for new accounts
      });
      await migrateLocalProgressIntoUser(newUser);
      
      return newUser;
    } catch (signUpError) {
      // If signup fails with "username already exists", query by username and set as current
      const errorMessage = signUpError?.message || String(signUpError || '');
      if (errorMessage.includes('Account already exists') || errorMessage.includes('username already')) {
        console.log("Username already exists, finding user...!!!!",username,appleUserId);



        
        try {
          const usernameQuery = new Parse.Query(Parse.User);
          usernameQuery.equalTo('username', username);
          const existingUsers = await usernameQuery.find();
          
          if (existingUsers.length > 0) {
            const existingUser = existingUsers[0];
            
            
            // Update appleId if not set
            if (existingUser.get('appleId') !== appleUserId) {
              existingUser.set('appleId', appleUserId);
              await existingUser.save();
            }
           
            // Set as current user
            Parse.User._clearCache();
            Parse.User._setCurrentUserCache(existingUser);
            
            try {
              await existingUser.fetch();
              // Fetch posibleCheater field for current user
              const possibleCheater = existingUser.get("possibleCheater") ?? false;
            } catch (error) {
              console.warn("Failed to fetch user:", error);
            }
            
            applyLocaleDataToUser(existingUser);
            await persistAuthContext(existingUser);
            await migrateLocalProgressIntoUser(existingUser);
            
            const stored = getStoredScores();
            const serverLastWonId = Number.isFinite(existingUser.get("lastWonId")) ? Number(existingUser.get("lastWonId")) : null;
            const serverLastPlayedId = Number.isFinite(existingUser.get("lastPlayedId")) ? Number(existingUser.get("lastPlayedId")) : null;
            overwriteStoredScores({
              score: existingUser.get("score") ?? 0,
              streak: existingUser.get("streak") ?? 0,
              maxStreak: existingUser.get("maxStreak") ?? 0,
              numbersPlayed: existingUser.get("numbersPlayed") ?? 0,
              lastPlayedId: serverLastPlayedId !== null ? serverLastPlayedId : (stored.lastPlayedId ?? null),
              lastWonId: serverLastWonId !== null ? serverLastWonId : (stored.lastWonId ?? null),
            });
            
            // Store credentials for next time
            if (typeof window !== "undefined") {
              window.localStorage.setItem('appleSignInUserId', appleUserId);
              window.localStorage.setItem('appleSignInUsername', username);
              // Note: We don't have the password, so user will need to sign in again next time
            }
            
            return existingUser;
          }
        } catch (queryError) {
          console.error("Failed to find existing user:", queryError);
        }
      }
      
      throw signUpError;
    }
  } catch (error) {
    console.error("Apple Sign In error:", error);
    throw error;
  }
}

// Auto sign in with Apple on reload (iOS only)
export async function autoSignInWithApple() {
  if (!isCapacitorIOS()) {
    return null;
  }

  try {
    const storedUserId = typeof window !== "undefined" ? window.localStorage.getItem('appleSignInUserId') : null;
    if (!storedUserId) {
      return null;
    }

    // Try to use stored username/password for auto-login
    const storedUsername = typeof window !== "undefined" ? window.localStorage.getItem('appleSignInUsername') : null;
    const storedPassword = typeof window !== "undefined" ? window.localStorage.getItem('appleSignInPassword') : null;
    
    if (storedUsername && storedPassword) {
      try {
        const user = await Parse.User.logIn(storedUsername, storedPassword);
        if (user) {
          console.log("Auto sign-in successful with stored username/password");
          
          try {
            await user.fetch();
            // Fetch posibleCheater field for current user
            const possibleCheater = user.get("possibleCheater") ?? false;
          } catch (error) {
            console.warn("Failed to refresh user on auto sign in", error);
          }

          // Track platform + publisher on auto-login as well
          await persistAuthContext(user);
          
          // FEATURE: BATTLE_MODE - Link Installation to user for push notifications
          try {
            const { linkInstallationToUser } = await import('./notificationService');
            await linkInstallationToUser();
          } catch (error) {
            console.warn('[Auth] Failed to link Installation to user:', error);
          }
          // END FEATURE: BATTLE_MODE
          
          // Only update locale data if user was just logged in (not on every auto-login)
          // This prevents unnecessary updatedAt changes when user just opens the app
          // applyLocaleDataToUser(user);
          
          // Refresh local storage from server
          const stored = getStoredScores();
          const serverLastWonId = Number.isFinite(user.get("lastWonId")) ? Number(user.get("lastWonId")) : null;
          const serverLastPlayedId = Number.isFinite(user.get("lastPlayedId")) ? Number(user.get("lastPlayedId")) : null;
          const serverScores = {
            score: user.get("score") ?? 0,
            streak: user.get("streak") ?? 0,
            maxStreak: user.get("maxStreak") ?? 0,
            numbersPlayed: user.get("numbersPlayed") ?? 0,
            lastPlayedId: serverLastPlayedId !== null ? serverLastPlayedId : (stored.lastPlayedId ?? null),
            lastWonId: serverLastWonId !== null ? serverLastWonId : (stored.lastWonId ?? null),
          };
          overwriteStoredScores(serverScores);
          
          return user;
        }
      } catch (loginError) {
        console.warn("Auto login with stored credentials failed:", loginError);
        // Don't clear stored credentials - they might still be valid for next time
      }
    }

    // Fallback: Check if Parse already has a valid session for this user
    const currentUser = Parse.User.current();
    if (currentUser) {
      // Check if current user has the matching appleId
      const currentAppleId = currentUser.get('appleId');
      if (currentAppleId === storedUserId) {
        // User is already logged in with matching Apple ID
        try {
          await currentUser.fetch();
          // Fetch posibleCheater field for current user
          const possibleCheater = currentUser.get("possibleCheater") ?? false;
        } catch (error) {
          console.warn("Failed to refresh user on auto sign in", error);
        }
        
        // Only update locale data if user was just logged in (not on every auto-login)
        // This prevents unnecessary updatedAt changes when user just opens the app
        // applyLocaleDataToUser(currentUser);
        
        // Refresh local storage from server
        const stored = getStoredScores();
        const serverLastWonId = Number.isFinite(currentUser.get("lastWonId")) ? Number(currentUser.get("lastWonId")) : null;
        const serverLastPlayedId = Number.isFinite(currentUser.get("lastPlayedId")) ? Number(currentUser.get("lastPlayedId")) : null;
        const serverScores = {
          score: currentUser.get("score") ?? 0,
          streak: currentUser.get("streak") ?? 0,
          maxStreak: currentUser.get("maxStreak") ?? 0,
          numbersPlayed: currentUser.get("numbersPlayed") ?? 0,
          lastPlayedId: serverLastPlayedId !== null ? serverLastPlayedId : (stored.lastPlayedId ?? null),
          lastWonId: serverLastWonId !== null ? serverLastWonId : (stored.lastWonId ?? null),
        };
        overwriteStoredScores(serverScores);
        
        return currentUser;
      }
    }

    return null;
  } catch (error) {
    console.warn("Auto sign in with Apple failed:", error);
    // Clear stored data on error
    if (typeof window !== "undefined") {
      window.localStorage.removeItem('appleSignInUserId');
      window.localStorage.removeItem('appleSignInIdentityToken');
      window.localStorage.removeItem('appleSignInPassword');
      window.localStorage.removeItem('appleSignInUsername');
    }
    return null;
  }
}

export function logOutUser() {
  return Parse.User.logOut();
}

export function getCurrentMonthScoreField() {
  const now = new Date();
  const monthNumber = now.getMonth() + 1; // 1-based
  const isEvenMonth = monthNumber % 2 === 0;
  return {
    fieldName: isEvenMonth ? "scoreMonthEven" : "scoreMonthOdd",
    isEvenMonth,
  };
}

export function getUserScores() {
  const user = Parse.User.current();
  if (!user) return null;
  const stored = getStoredScores();
  return {
    score: user.get("score") ?? stored.score ?? 0,
    streak: user.get("streak") ?? stored.streak ?? 0,
    maxStreak: user.get("maxStreak") ?? stored.maxStreak ?? 0,
    numbersPlayed: user.get("numbersPlayed") ?? stored.numbersPlayed ?? 0,
  };
}

export function getUserFinishedIds() {
  const user = Parse.User.current();
  if (!user) return [];
  const finished = user.get(FINISHED_IDS_KEY);
  return Array.isArray(finished) ? finished : [];
}

export async function addUserFinishedId(idNumber) {
  const user = Parse.User.current();
  if (!user) return [];
  const current = getUserFinishedIds();
  if (current.includes(idNumber)) {
    return current;
  }
  const next = [...current, idNumber];
  user.set(FINISHED_IDS_KEY, next);
  await user.save();
  return next;
}

/**
 * Save play sequence data for a game (all actions in chronological order)
 * @param {number} idNumber - The game idNumber
 * @param {Array} actions - Array of action objects in chronological order:
 *   - Click: {type: 'click', r, c, timestamp}
 *   - Guess: {type: 'guess', word, timestamp, correct}
 * 
 * Actions are stored in compact format:
 *   - Clicks: "r{row}c{col}" (e.g., "r1c0")
 *   - Guesses: word string (e.g., "MUS")
 */
export async function savePlaySequence(idNumber, actions) {
  const user = Parse.User.current();
  if (!user) return;
  
  if (typeof idNumber !== "number" || !Number.isFinite(idNumber)) {
    console.warn("Invalid idNumber for playSequence:", idNumber);
    return;
  }
  
  // Compress actions to compact format
  const compactActions = (Array.isArray(actions) ? actions : []).map(action => {
    if (action.type === 'click') {
      // Format: "r{row}c{col}"
      return `r${action.r}c${action.c}`;
    } else if (action.type === 'guess') {
      // Format: just the word
      return action.word;
    }
    // Fallback: return as string if unknown type
    return String(action);
  });
  
  // Get existing playSequences array or initialize as empty array
  const existingPlaySequences = user.get("playSequences");
  let playSequencesArray = Array.isArray(existingPlaySequences) ? existingPlaySequences : [];
  
  // Convert array to object for easier lookup (using idNumber as key)
  const playSequencesDict = {};
  for (const seq of playSequencesArray) {
    if (seq && typeof seq.idNumber === "number") {
      playSequencesDict[seq.idNumber] = seq;
    }
  }
  
  // Create or update the sequence for this idNumber
  playSequencesDict[idNumber] = {
    idNumber: idNumber,
    actions: compactActions,
  };
  
  // Convert back to array format (Parse prefers arrays)
  playSequencesArray = Object.values(playSequencesDict);
  
  user.set("playSequences", playSequencesArray);
  
  try {
    await user.save();
    console.log(`Play sequence saved for idNumber ${idNumber}`);
  } catch (error) {
    console.error("Failed to save play sequence to Parse:", error);
    throw error;
  }
}

export async function fetchCompetitionLeaderboard({ limit = 1000 } = {}) {
  const { fieldName } = getCurrentMonthScoreField();
  const currentUser = Parse.User.current();

  // Get friend IDs from current user's friends relation via cloud function
  let friendIds = [];
  if (currentUser) {
    try {
      // Use cloud function to get friends (needs masterKey)
      const result = await Parse.Cloud.run("getUserFriends");
      if (result && Array.isArray(result.friendIds)) {
        friendIds = result.friendIds;
      }
    } catch (error) {
      console.warn("Error fetching friends for leaderboard:", error);
      // Continue without friends if there's an error
    }
  }

  try {
    const response = await Parse.Cloud.run("getCompetitionLeaderboard", {
      fieldName,
      limit,
      friendIds, // Pass friend IDs to cloud function
      includeOppositeMonth: true, // Request new format with opposite month data
    });

    // Handle both old format (array) and new format (object with leaderboard and oppositeMonthTop3)
    let mainLeaderboard = [];
    let oppositeMonthTop3 = [];
    
    if (Array.isArray(response)) {
      // Old format - backward compatibility
      mainLeaderboard = response;
    } else if (response && Array.isArray(response.leaderboard)) {
      // New format
      mainLeaderboard = response.leaderboard;
      oppositeMonthTop3 = Array.isArray(response.oppositeMonthTop3) ? response.oppositeMonthTop3 : [];
    } else {
      return { entries: [], oppositeMonthTop3: [] };
    }

    const processedEntries = mainLeaderboard.map((entry) => {
      const monthlyScore = Number.isFinite(entry?.monthlyScore)
        ? entry.monthlyScore
        : Number(entry?.monthlyScore) || 0;

      // Get playedMonthOdd or playedMonthEven based on current month
      const { isEvenMonth } = getCurrentMonthScoreField();
      const playedFieldName = isEvenMonth ? "playedMonthEven" : "playedMonthOdd";
      const playedCount = Number.isFinite(entry?.[playedFieldName])
        ? Number(entry[playedFieldName])
        : 0;

      let avatarUrl = null;
      const avatarValue = entry?.avatar;
      if (avatarValue && typeof avatarValue === "object") {
        if (typeof avatarValue.url === "string") {
          avatarUrl = avatarValue.url;
        } else if (typeof avatarValue._url === "string") {
          avatarUrl = avatarValue._url;
        }
      } else if (typeof avatarValue === "string") {
        avatarUrl = avatarValue;
      } else if (entry?.avatarUrl) {
        avatarUrl = entry.avatarUrl;
      }

      const name = entry?.name || "Player";

      const isCurrentUser =
        typeof entry?.isCurrentUser === "boolean"
          ? entry.isCurrentUser
          : Boolean(currentUser && entry?.objectId === currentUser.id);

      const isFriend = Boolean(entry?.isFriend);

      // Calculate currentIdNumber locally based on user's local date
      const fromDate = new Date(2022, 0, 1);
      const currentDate = new Date();
      const utcCurrent = Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      );
      const utcFrom = Date.UTC(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate()
      );
      const localCurrentIdNumber = Math.floor((utcCurrent - utcFrom) / (1000 * 60 * 60 * 24));

      // Compare lastPlayedId with local currentIdNumber (based on user's local date)
      const lastPlayedId = entry?.lastPlayedId !== undefined && Number.isFinite(entry.lastPlayedId) 
        ? Number(entry.lastPlayedId) 
        : null;
      let playedToday = false;
      let playedFuture = false;
      
      if (lastPlayedId !== null) {
        if (lastPlayedId === localCurrentIdNumber) {
          playedToday = true;
        } else if (lastPlayedId > localCurrentIdNumber) {
          playedFuture = true;
        }
      }

      // Get lastScore if available (0-10, or null/undefined if not set)
      const lastScore = entry?.lastScore !== undefined && entry?.lastScore !== null
        ? (Number.isFinite(entry.lastScore) ? Number(entry.lastScore) : null)
        : undefined;

      return {
        id: entry?.objectId || name,
        name,
        monthlyScore,
        playedCount,
        avatarUrl,
        isCurrentUser,
        isFriend,
        playedToday,
        playedFuture,
        lastScore, // undefined if not available, number (0-10) if available, null if explicitly set to null
      };
    });

    // Process opposite month top 3
    const processedOppositeTop3 = oppositeMonthTop3.map((entry) => {
      const monthlyScore = Number.isFinite(entry?.monthlyScore)
        ? entry.monthlyScore
        : Number(entry?.monthlyScore) || 0;

      // Get playedMonthOdd or playedMonthEven based on opposite month
      const { isEvenMonth } = getCurrentMonthScoreField();
      const oppositeIsEvenMonth = !isEvenMonth; // Opposite month
      const playedFieldName = oppositeIsEvenMonth ? "playedMonthEven" : "playedMonthOdd";
      const playedCount = Number.isFinite(entry?.[playedFieldName])
        ? Number(entry[playedFieldName])
        : 0;

      let avatarUrl = null;
      const avatarValue = entry?.avatar;
      if (avatarValue && typeof avatarValue === "object") {
        if (typeof avatarValue.url === "string") {
          avatarUrl = avatarValue.url;
        } else if (typeof avatarValue._url === "string") {
          avatarUrl = avatarValue._url;
        }
      } else if (typeof avatarValue === "string") {
        avatarUrl = avatarValue;
      } else if (entry?.avatarUrl) {
        avatarUrl = entry.avatarUrl;
      }

      const name = entry?.name || "Player";

      const isCurrentUser =
        typeof entry?.isCurrentUser === "boolean"
          ? entry.isCurrentUser
          : Boolean(currentUser && entry?.objectId === currentUser.id);

      const isFriend = Boolean(entry?.isFriend);

      // Calculate currentIdNumber locally based on user's local date
      const fromDate = new Date(2022, 0, 1);
      const currentDate = new Date();
      const utcCurrent = Date.UTC(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        currentDate.getDate()
      );
      const utcFrom = Date.UTC(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate()
      );
      const localCurrentIdNumber = Math.floor((utcCurrent - utcFrom) / (1000 * 60 * 60 * 24));

      // Compare lastPlayedId with local currentIdNumber (based on user's local date)
      const lastPlayedId = entry?.lastPlayedId !== undefined && Number.isFinite(entry.lastPlayedId) 
        ? Number(entry.lastPlayedId) 
        : null;
      let playedToday = false;
      let playedFuture = false;
      
      if (lastPlayedId !== null) {
        if (lastPlayedId === localCurrentIdNumber) {
          playedToday = true;
        } else if (lastPlayedId > localCurrentIdNumber) {
          playedFuture = true;
        }
      }

      // Get lastScore if available (0-10, or null/undefined if not set)
      const lastScore = entry?.lastScore !== undefined && entry?.lastScore !== null
        ? (Number.isFinite(entry.lastScore) ? Number(entry.lastScore) : null)
        : undefined;

      return {
        id: entry?.objectId || name,
        name,
        monthlyScore,
        rank: entry?.rank || null, // Rank from cloud function (1, 2, or 3 based on score)
        playedCount,
        avatarUrl,
        isCurrentUser,
        isFriend,
        playedToday,
        playedFuture,
        lastScore,
      };
    });

    return {
      entries: processedEntries,
      oppositeMonthTop3: processedOppositeTop3,
    };
  } catch (error) {
    console.warn("getCompetitionLeaderboard failed", error);
    throw error;
  }
}

export async function requestPasswordReset(emailOrName) {
  const trimmedInput = String(emailOrName || "").trim();
  
  if (!trimmedInput) {
    throw new Error("Please enter your email or name.");
  }

  // FEATURE: PASSWORD_RESET_WITH_NAME_OR_EMAIL
  // Support password reset with either email or name
  let userEmail = null;
  
  // Check if input is an email (contains @)
  const isEmail = trimmedInput.includes("@");
  
  if (isEmail) {
    // Use email directly (normalized to lowercase)
    userEmail = normalizeEmail(trimmedInput);
  } else {
    // Input is a name - use Cloud Function to find username/email by name
    try {
      const result = await Parse.Cloud.run("findUsernameByName", {
        name: trimmedInput,
      });
      
      if (!result || !result.found || !result.username) {
        throw new Error("No account found with this name or email.");
      }
      
      // Use the username (which is the email) from the result
      userEmail = result.username;
    } catch (error) {
      if (error.message && error.message.includes("No account found")) {
        throw error;
      }
      console.error("Error finding user by name for password reset:", error);
      throw new Error("No account found with this name or email.");
    }
  }
  
  if (!userEmail) {
    throw new Error("Please enter a valid email or name.");
  }
  // END FEATURE: PASSWORD_RESET_WITH_NAME_OR_EMAIL

  await Parse.User.requestPasswordReset(userEmail);
  return {
    email: userEmail,
  };
}

// Add a user to friends (Parse Relation)
export async function addFriend(usernameOrEmail, lang = "en") {
  const trimmedInput = String(usernameOrEmail || "").trim();
  
  if (!trimmedInput) {
    throw new Error(lang === "nl" 
      ? "Voer een gebruikersnaam of e-mailadres in." 
      : "Please enter a username or email address.");
  }

  const currentUser = Parse.User.current();
  if (!currentUser) {
    throw new Error(lang === "nl" 
      ? "Je moet ingelogd zijn om vrienden toe te voegen." 
      : "You must be logged in to add friends.");
  }

  try {
    // Find the user to add
    const result = await Parse.Cloud.run("findUserByUsernameOrEmail", {
      input: trimmedInput,
    });

    if (!result || !result.found || !result.userId) {
      throw new Error(lang === "nl" 
        ? "Geen account gevonden met deze gebruikersnaam of e-mailadres." 
        : "No account found with this username or email address.");
    }

    const friendUserId = result.userId;
    let friendUsername = trimmedInput; // Fallback to input

    // Don't allow adding yourself
    if (friendUserId === currentUser.id) {
      throw new Error(lang === "nl" 
        ? "Je kunt jezelf niet als vriend toevoegen." 
        : "You cannot add yourself as a friend.");
    }

    // Get or create the friends relation
    const friendsRelation = currentUser.relation("friends");
    
    // Check if already a friend (no need for useMasterKey - user can read their own friends)
    const query = friendsRelation.query();
    query.equalTo("objectId", friendUserId);
    const existingFriends = await query.find();
    
    if (existingFriends.length > 0) {
      throw new Error(lang === "nl" 
        ? "Deze gebruiker staat al in je vriendenlijst." 
        : "This user is already in your friends list.");
    }

    // Add the friend
    const friendUser = Parse.User.createWithoutData(friendUserId);
    friendsRelation.add(friendUser);
    
    // Save the relation (user can save their own user object)
    await currentUser.save();
    
    // Refresh the current user to ensure friends relation is updated
    await currentUser.fetch();
    
    // Fetch the friend's name for the success message
    try {
      const friendQuery = new Parse.Query(Parse.User);
      friendQuery.select("name");
      const friend = await friendQuery.get(friendUserId);
      friendUsername = friend.get("name") || friendUsername;
    } catch (err) {
      console.warn("Could not fetch friend name:", err);
    }

    return {
      success: true,
      userId: friendUserId,
      username: friendUsername,
    };
  } catch (error) {
    if (error.message) {
      throw error;
    }
    console.error("Error adding friend:", error);
    throw new Error(lang === "nl" 
      ? "Kon vriend niet toevoegen. Probeer het opnieuw." 
      : "Could not add friend. Please try again.");
  }
}

export async function removeFriendById(userId, lang = "en") {
  if (!userId || typeof userId !== "string") {
    throw new Error(lang === "nl" 
      ? "Ongeldige gebruiker ID." 
      : "Invalid user ID.");
  }

  const currentUser = Parse.User.current();
  if (!currentUser) {
    throw new Error(lang === "nl" 
      ? "Je moet ingelogd zijn om vrienden te verwijderen." 
      : "You must be logged in to remove friends.");
  }

  try {
    // Refresh current user first to ensure friends relation is up-to-date
    await currentUser.fetch();
    
    // Get the friends relation
    const friendsRelation = currentUser.relation("friends");
    
    // Remove the friend (even if not found, it's safe to call remove)
    const friendUser = Parse.User.createWithoutData(userId);
    friendsRelation.remove(friendUser);
    
    // Save the relation
    await currentUser.save();
    
    // Refresh the current user to ensure friends relation is updated
    await currentUser.fetch();

    // Fetch the friend's username for the success message
    let friendUsername = userId;
    try {
      const friendQuery = new Parse.Query(Parse.User);
      friendQuery.select("username", "name");
      const friend = await friendQuery.get(userId);
      const username = friend.get("username");
      const name = friend.get("name");
      if (username && username.trim() && username !== userId) {
        friendUsername = username;
      } else if (name && name.trim()) {
        friendUsername = name;
      }
    } catch (err) {
      console.warn("Could not fetch friend username:", err);
    }

    return {
      success: true,
      userId: userId,
      username: friendUsername,
    };
  } catch (error) {
    if (error.message) {
      throw error;
    }
    console.error("Error removing friend:", error);
    throw new Error(lang === "nl" 
      ? "Kon vriend niet verwijderen. Probeer het opnieuw." 
      : "Could not remove friend. Please try again.");
  }
}

export async function addFriendById(userId, lang = "en") {
  if (!userId || typeof userId !== "string") {
    throw new Error(lang === "nl" 
      ? "Ongeldige gebruiker ID." 
      : "Invalid user ID.");
  }

  const currentUser = Parse.User.current();
  if (!currentUser) {
    throw new Error(lang === "nl" 
      ? "Je moet ingelogd zijn om vrienden toe te voegen." 
      : "You must be logged in to add friends.");
  }

  try {
    // Don't allow adding yourself
    if (userId === currentUser.id) {
      throw new Error(lang === "nl" 
        ? "Je kunt jezelf niet als vriend toevoegen." 
        : "You cannot add yourself as a friend.");
    }

    // Get or create the friends relation
    const friendsRelation = currentUser.relation("friends");
    
    // Check if already a friend
    const query = friendsRelation.query();
    query.equalTo("objectId", userId);
    const existingFriends = await query.find();
    
    if (existingFriends.length > 0) {
      throw new Error(lang === "nl" 
        ? "Deze gebruiker staat al in je vriendenlijst." 
        : "This user is already in your friends list.");
    }

    // Add the friend
    const friendUser = Parse.User.createWithoutData(userId);
    friendsRelation.add(friendUser);
    
    // Save the relation
    await currentUser.save();
    
    // Refresh the current user to ensure friends relation is updated
    await currentUser.fetch();

    // Fetch the friend's username for the success message
    let friendUsername = userId;
    try {
      const friendQuery = new Parse.Query(Parse.User);
      friendQuery.select("username", "name");
      const friend = await friendQuery.get(userId);
      // Prefer username (if it exists and is not empty), fallback to name, then to userId
      const username = friend.get("username");
      const name = friend.get("name");
      if (username && username.trim() && username !== userId) {
        friendUsername = username;
      } else if (name && name.trim()) {
        friendUsername = name;
      }
    } catch (err) {
      console.warn("Could not fetch friend username:", err);
    }

    return {
      success: true,
      userId: userId,
      username: friendUsername,
    };
  } catch (error) {
    if (error.message) {
      throw error;
    }
    console.error("Error adding friend:", error);
    throw new Error(lang === "nl" 
      ? "Kon vriend niet toevoegen. Probeer het opnieuw." 
      : "Could not add friend. Please try again.");
  }
}

